/**
 * Signup types and pure utility functions.
 * No React or component dependencies — components import from here, not the other way around.
 */
import { Event, Shift, ShiftType } from '@amzn/red-velvet-api';
import { isUnlistedEvent } from './eventUtils';

export enum SignupStatus {
  SIGNUP = 'Signed up',
  CANCEL = 'Signup Cancelled',
  PENDING = 'Pending',
}

export interface AmazonSignup {
  signupId: string;
  shiftId: string;
  name: string;
  alias: string;
  email: string;
  building: string;
  managerLogin: string;
  numberOfGuests: number;
  tShirtSize: string;
  signupStatus: SignupStatus;
}

export function isShiftManageSignupBlocked(event?: Event, shift?: Shift): boolean {
  return (
    (event !== undefined && isUnlistedEvent(event)) ||
    shift?.shiftType === ShiftType.WAITLIST ||
    shift?.shiftType === ShiftType.DROP_IN
  );
}

export enum SignupBlockedReason {
  SIGNUPS_CLOSED,
  SHIFT_FULL,
  REQUIRED_WAIVER,
  CUSTOM_QUESTIONS,
  TSHIRT_REQUIRED,
  UNSUPPORTED_SHIFT_TYPE,
}

export function getSignupBlockedReasons(
  event: Event,
  shift: Shift,
  inlineCancels: number
): SignupBlockedReason[] {
  const reasons: SignupBlockedReason[] = [];
  if (isShiftManageSignupBlocked(event, shift))
    reasons.push(SignupBlockedReason.UNSUPPORTED_SHIFT_TYPE);
  if (!shift.signupsOpen) reasons.push(SignupBlockedReason.SIGNUPS_CLOSED);
  if (shift.maxSignup > 0 && shift.signupsRemaining + inlineCancels <= 0)
    reasons.push(SignupBlockedReason.SHIFT_FULL);
  if (event.waiver && event.waiver.acceptance === 'required')
    reasons.push(SignupBlockedReason.REQUIRED_WAIVER);
  if (event.questions && event.questions.length > 0)
    reasons.push(SignupBlockedReason.CUSTOM_QUESTIONS);
  if (event.hasTShirt) reasons.push(SignupBlockedReason.TSHIRT_REQUIRED);
  return reasons;
}

export const SIGNUP_CSV_HEADERS = [
  'Shift Name',
  'Shift Start Date Time',
  'Shift End Date Time',
  'Name',
  'Email Address',
  'Building',
  'T-shirt Size',
  'Number of Guests',
];

export function formatDateTimeForCsv(date: Date | undefined, timezone: string): string {
  if (!date) return '';
  return date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: timezone,
  });
}

export function buildSignupCsvRows(
  signups: AmazonSignup[],
  shift: Shift,
  event: Event,
  contentTimezone: string
): string[][] {
  const shiftName = shift.name || '';
  const startDate = shift.startDateTime || event.startTimestamp;
  const endDate = shift.endDateTime || event.endTimestamp;

  return signups.map((signup) => [
    shiftName,
    formatDateTimeForCsv(startDate, contentTimezone),
    formatDateTimeForCsv(endDate, contentTimezone),
    signup.name,
    signup.email,
    signup.building,
    signup.tShirtSize,
    signup.numberOfGuests ? String(signup.numberOfGuests) : '',
  ]);
}

export function formatDateTimeForFilename(date: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value || '';

  return `${get('year')}-${get('month')}-${get('day')}-${get('hour')}${get('minute')}`;
}

export function buildSignupCsvFilename(
  eventId: string,
  shiftName: string,
  filenameTimezone: string
): string {
  // Remove non-ASCII and filesystem-unsafe characters (e.g. Japanese chars, #, /, :)
  const sanitizedShiftName = (shiftName || '').replace(/[^\w\s-]/g, '').trim();
  const timestamp = formatDateTimeForFilename(new Date(), filenameTimezone);
  return `Signups-${eventId}-${sanitizedShiftName}-${timestamp}.csv`;
}
